import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { X } from 'react-feather';

type SnackbarVariant = 'success' | 'error' | 'info';

interface SnackbarOptions {
  variant?: SnackbarVariant;
  durationMs?: number;
}

interface SnackbarMessage {
  id: number;
  message: string;
  variant: SnackbarVariant;
  durationMs: number;
}

interface SnackbarContextValue {
  showSnackbar: (message: string, options?: SnackbarOptions) => void;
  hideSnackbar: () => void;
}

const SnackbarContext = createContext<SnackbarContextValue | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [snackbar, setSnackbar] = useState<SnackbarMessage | null>(null);

  const hideSnackbar = useCallback(() => {
    setSnackbar(null);
  }, []);

  const showSnackbar = useCallback((message: string, options: SnackbarOptions = {}) => {
    setSnackbar({
      id: Date.now(),
      message,
      variant: options.variant ?? 'info',
      durationMs: options.durationMs ?? 3500,
    });
  }, []);

  useEffect(() => {
    if (!snackbar) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setSnackbar(null);
    }, snackbar.durationMs);

    return () => window.clearTimeout(timeoutId);
  }, [snackbar]);

  const value = useMemo(
    () => ({
      showSnackbar,
      hideSnackbar,
    }),
    [hideSnackbar, showSnackbar]
  );

  return (
    <SnackbarContext.Provider value={value}>
      {children}
      {snackbar && (
        <div className="snackbar-region" aria-live={snackbar.variant === 'error' ? 'assertive' : 'polite'}>
          <div
            key={snackbar.id}
            className={`snackbar snackbar-${snackbar.variant}`}
            role={snackbar.variant === 'error' ? 'alert' : 'status'}
          >
            <span className="snackbar-message">{snackbar.message}</span>
            <button
              type="button"
              className="snackbar-dismiss"
              onClick={hideSnackbar}
              aria-label="Dismiss notification"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextValue => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
